import { useEffect, useState } from 'react';
import {
  RiCheckLine,
  RiCloseLine,
  RiFileCopyLine,
  RiLink,
  RiLinkM,
  RiLockLine,
  RiRefreshLine,
  RiShieldCheckLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../state/api';
import { useStore } from '../state/store';
import type { ShareLink } from '../state/domain';
import { cn, formatDate } from '@/lib/utils';

export function ShareDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const engagementId = useStore(state => state.activeEngagementId);
  const engagement = useStore(state => state.engagements.find(e => e.id === state.activeEngagementId));
  const toast = useStore(state => state.toast);

  const [items, setItems] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState('只读演示');
  const [days, setDays] = useState(14);
  const [copied, setCopied] = useState<string | null>(null);

  const refresh = async () => {
    if (!engagementId) return;
    setLoading(true);
    try {
      const { items: list } = await api.listShares(engagementId);
      setItems(list);
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engagementId]);

  const create = async () => {
    if (!engagementId) return;
    try {
      await api.createShare({ engagementId, label, expiresInDays: days });
      toast('success', '已生成只读共享链接');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const revoke = async (token: string) => {
    try {
      await api.revokeShare(token);
      toast('success', '共享链接已撤销');
      await refresh();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const copy = async (token: string) => {
    const url = `${window.location.origin}/?share=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      window.prompt('复制此链接：', url);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="w-[min(660px,95vw)]">
        <DialogHeader>
          <DialogTitle>共享演示链接</DialogTitle>
          <DialogDescription>
            为「{engagement?.name ?? '当前案件'}」生成只读链接。持有链接的人可查看关联墙与档案，
            <strong className="text-foreground"> 不能编辑、不能查看审计日志与用户信息</strong>，随时可撤销。
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 flex gap-2.5 rounded-[var(--radius)] border border-border bg-muted/40 p-3">
          <RiShieldCheckLine className="mt-0.5 size-4 shrink-0 text-[var(--color-evidence)]" />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            共享通道仅返回该案件的展示快照（对象 / 线索 / 证据 / 研判 / 时间线 / 画布），
            不包含会话令牌、密钥、审计记录与账号信息。链接默认 {days} 天有效。
          </p>
        </div>

        <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
          <div className="flex flex-col gap-1.5">
            <Label>备注名称</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="例如：给客户看的演示" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>有效期（天）</Label>
            <Input
              type="number"
              min={1}
              max={365}
              className="w-[92px]"
              value={days}
              onChange={e => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 14)))}
            />
          </div>
          <Button onClick={create}>
            <RiLinkM className="size-3.5" />
            生成链接
          </Button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="label-caps">已生成的链接（{items.length}）</span>
          <Button size="xs" variant="ghost" onClick={refresh} disabled={loading}>
            <RiRefreshLine className={cn('size-3', loading && 'animate-spin')} />
            刷新
          </Button>
        </div>

        <div className="mt-2 flex flex-col gap-1.5">
          {items.map(item => {
            const expired = item.expiresAt ? new Date(item.expiresAt).getTime() < Date.now() : false;
            const dead = item.revoked || expired;
            return (
              <div
                key={item.token}
                className={cn(
                  'flex items-center gap-2 rounded-[var(--radius)] border p-2.5',
                  dead ? 'border-border bg-muted/40 opacity-70' : 'border-border bg-card'
                )}
              >
                <RiLink className="size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[11.5px] font-medium">{item.label ?? '只读演示'}</span>
                    {item.revoked && <Badge variant="cord">已撤销</Badge>}
                    {!item.revoked && expired && <Badge variant="cord">已过期</Badge>}
                    {!dead && <Badge variant="evidence">有效</Badge>}
                  </div>
                  <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                    ?share={item.token} · 至 {formatDate(item.expiresAt)}
                  </div>
                </div>
                <Button
                  size="xs"
                  variant="outline"
                  disabled={dead}
                  onClick={() => copy(item.token)}
                  className="shrink-0"
                >
                  {copied === item.token ? (
                    <>
                      <RiCheckLine className="size-3" />
                      已复制
                    </>
                  ) : (
                    <>
                      <RiFileCopyLine className="size-3" />
                      复制链接
                    </>
                  )}
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  disabled={dead}
                  title="撤销"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => revoke(item.token)}
                >
                  <RiCloseLine className="size-3.5" />
                </Button>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
              还没有共享链接。生成后可直接把链接发给演示对象。
            </div>
          )}
        </div>

        <p className="mt-4 flex items-center gap-1.5 border-t border-dashed border-border pt-3 text-[10.5px] text-muted-foreground">
          <RiLockLine className="size-3" />
          共享访问会记入审计日志（share.create / share.revoke）。
        </p>
      </DialogContent>
    </Dialog>
  );
}